import * as React from 'react';
import { Tooltip } from 'reactstrap';

type Placement = React.ComponentProps<typeof Tooltip>['placement'];

interface AppTooltipProps {
  /** Tooltip content. When falsy, the children render without a tooltip. */
  content?: React.ReactNode;
  /** Popper placement. Default 'top'. */
  placement?: Placement;
  /**
   * `display` of the wrapping <span>. Default 'inline-flex' keeps inline targets
   * (buttons, icons) laid out as before; pass 'block' for block-level targets
   * such as a FormGroup so the stacked layout is preserved.
   */
  display?: React.CSSProperties['display'];
  /** Optional class applied to the wrapping <span>. */
  className?: string;
  children: React.ReactNode;
}

/**
 * Tooltip bound to its child via a `ref` (not a global `id`) with controlled
 * open state driven by the element's own hover/focus.
 *
 * Why this shape:
 *  - reactstrap's `Tooltip`/`UncontrolledTooltip` throw
 *    "The target ... could not be identified in the dom" when a *string* target
 *    isn't mounted (reactstrap #1593). A `ref` target reads `ref.current`
 *    instead and never throws, so no `doesTargetExist` guard is needed.
 *  - The `Tooltip` is rendered only while `open`, and `open` lives in this
 *    component tied to the element's hover. A single pointer means at most one
 *    tooltip is open, React keeps the leave handler bound to the live node
 *    across re-renders, and if the element unmounts the tooltip unmounts with
 *    it. That removes the stuck/duplicate tooltips seen with the uncontrolled,
 *    id-bound version inside frequently re-rendering tables.
 *  - Popper portals the tooltip to <body>, so it isn't clipped by `overflow`
 *    on the surrounding card/table containers.
 *  - `trigger="manual"`: this component already opens and closes the tooltip
 *    from the element's own hover/focus, so reactstrap's own hover/focus
 *    listeners on the same element would be a second controller of one flag.
 *  - `boundariesElement="viewport"`: the tooltip lives in <body>, so the only
 *    box it must stay inside is the viewport. The default (`scrollParent`)
 *    kept it inside the table's scroll box and, on a row near the top of the
 *    table, pushed a tall tooltip down over the element being hovered.
 *  - `pointer-events: none` on the popper (`.app-tooltip-popper` in
 *    `_tooltips.scss`): a tooltip that lands under the pointer would take the
 *    hover from its element, close, give the hover back, reopen, and flicker.
 *    Tooltips auto-hide, so nobody can interact with them anyway.
 */
export const AppTooltip: React.FC<AppTooltipProps> = ({ content, placement = 'top', display = 'inline-flex', className, children }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <span
        ref={ref}
        className={className}
        style={{ display }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && content ? (
        <Tooltip
          target={ref}
          isOpen
          trigger="manual"
          placement={placement}
          boundariesElement="viewport"
          fade={false}
          delay={{ show: 0, hide: 0 }}
          popperClassName="app-tooltip-popper"
          innerClassName="app-tooltip"
        >
          {content}
        </Tooltip>
      ) : null}
    </>
  );
};
